import clsx from "clsx";
import { IoSparkles } from "react-icons/io5";
import Link from "next/link";
import { motion, useAnimation } from "framer-motion";

const BookButton = ({
  color,
  text,
  link,
  custom,
  icon,
  left,
  right,
  onClick,
  iconColor,
}) => {
  const buttonControls = useAnimation();
  const textControls = useAnimation();
  const shimmerControls = useAnimation();

  const gradientClass = clsx(
    color === "blue" && "from-vblue1 via-vblue2 to-vcyan1",
    color === "white" && "from-white to-white [&_span]:text-black",
    color === "orange" && "from-vrose via-vred to-vorange",
    color === "purple" && "from-violet-500 via-indigo-500 to-blue-500",
    color === "green" && "from-shopify2 to-shopify3",

    // added remaining hues
    color === "slate" && "from-[#94A3B8] via-[#475569] to-[#1E293B]",
    color === "gray" && "from-[#9CA3AF] via-[#4B5563] to-[#1F2937]",
    color === "zinc" && "from-[#A1A1AA] via-[#52525B] to-[#27272A]",
    color === "neutral" && "from-[#A3A3A3] via-[#525252] to-[#262626]",
    color === "stone" && "from-[#A8A29E] via-[#57534E] to-[#292524]",
    color === "red" && "from-[#F87171] via-[#EF4444] to-[#B91C1C]",
    color === "amber" && "from-[#FBBF24] via-[#D97706] to-[#92400E]",
    color === "yellow" && "from-[#FDE047] via-[#FACC15] to-[#CA8A04]",
    color === "lime" && "from-[#A3E635] via-[#65A30D] to-[#3F6212]",
    color === "emerald" && "from-[#34D399] via-[#10B981] to-[#065F46]",
    color === "teal" && "from-[#2DD4BF] via-[#14B8A6] to-[#0F766E]",
    color === "cyan" && "from-[#22D3EE] via-[#06B6D4] to-[#0E7490]",
    color === "sky" && "from-[#38BDF8] via-[#0284C7] to-[#075985]",
    color === "indigo" && "from-[#818CF8] via-[#6366F1] to-[#4338CA]",
    color === "violet" && "from-[#A78BFA] via-[#8B5CF6] to-[#4C1D95]",
    color === "fuchsia" && "from-[#E879F9] via-[#D946EF] to-[#86198F]",
    color === "pink" && "from-[#F472B6] via-[#EC4899] to-[#BE185D]",
    color === "rose" && "from-[#FB7185] via-[#F43F5E] to-[#BE123C]",
  );

  return (
    <Link href={link ? link : "https://calendly.com/vwd/free-consultation"}>
      <motion.div
        {...(onClick ? { onClick } : {})}
        onHoverStart={() => {
          buttonControls.start({
            scaleX: 1.03,
            scaleY: 1.08,
            transition: { type: "spring", bounce: 0.65, duration: 0.625 },
          });
          textControls.start({
            scaleX: 1 / 1.03,
            scaleY: 1 / 1.08,
            transition: { type: "spring", bounce: 0.65, duration: 0.625 },
          });
          shimmerControls.start({
            x: ["-120%", "120%"],
            opacity: [1, 1, 0],
            transition: { duration: 0.7, ease: "easeInOut" },
          });
        }}
        onHoverEnd={() => {
          buttonControls.start({
            scaleX: 1,
            scaleY: 1,
            transition: { type: "spring", bounce: 0.65, duration: 0.625 },
          });
          textControls.start({
            scaleX: 1,
            scaleY: 1,
            transition: { type: "spring", bounce: 0.65, duration: 0.625 },
          });
          shimmerControls.stop();
          shimmerControls.set({ x: "-120%", opacity: 0 });
        }}
        animate={buttonControls}
        style={{ willChange: "transform" }}
        className={clsx(
          "backface-visibility-hidden relative mx-auto origin-center justify-center gap-2.5 overflow-hidden rounded-full bg-gradient-to-br px-5 py-3 font-bold text-white active:top-px",
          gradientClass,
          custom || "",
        )}
      >
        {/* Shimmer effect overlay */}
        <motion.div
          animate={shimmerControls}
          initial={{ x: "-120%", opacity: 0 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 1,
            background:
              "linear-gradient(60deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0) 70%)",
            borderRadius: "inherit",
            filter: "blur(8px)",
          }}
        />
        <motion.span
          animate={textControls}
          style={{ willChange: "transform" }}
          className={clsx(
            "flex origin-center items-center gap-2.5 whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
            color !== "white" && "",
          )}
        >
          {left && !right && icon ? icon : null}
          {!left && !right ? (
            <IoSparkles className={clsx(iconColor && iconColor)} />
          ) : null}
          {text ? text : "Book an Appointment"}
          <span className={iconColor ? iconColor : ""}>
            {right && icon ? icon : null}
          </span>
        </motion.span>
      </motion.div>
    </Link>
  );
};

export default BookButton;
